/* SEGMENTS ARE BUILT ON THE RISK MODEL, not on health bands.
 *
 * Until 2026-09-04 they cut `health` at 40 / 69 / 85, so a segment could name a
 * different set of people than the console's "Going quiet" panel, and two of them
 * could pick up a member on Care Track.
 *
 * SUPPRESSION IS ENFORCED HERE AND IT IS ENFORCED FIRST. A segment is a campaign
 * audience and a campaign is an automated message, so a member on Care Track or Hold
 * must never come out of this function unless the rule is specifically about them -
 * and no campaign is allowed to point at that one. It exists so a REVIEW list can be
 * built, which is the opposite of a send list. Suspended is frozen, and frozen is Care
 * Track or Hold, so a suspended member is out whatever the rule says.
 *
 * FIRST-90-DAY MEMBERS ARE OFF EVERY RISK SEGMENT (SITE-RULES 3b). They have no rhythm
 * to drift from yet, so a score computed for them is a number about nothing.
 *
 * A DESCRIPTION NAMES THE PEOPLE AND THE MOVE, never a threshold. "Members drifting
 * from their own rhythm. A call from their anchor, this week." is a segment somebody
 * can act on; "health score < 40" is a query.
 */
import { ONBOARDING_DAYS } from './lifecycle.js';
import { riskModel } from './risk.js';
import { cancellationOpen } from './cancellation.js';
import { daysSince, todayISO } from './time.js';
/* ── DORMANT: away for months, still on the books ────────────────────────────
 *
 * Anuj, 2026-09-15: "if a member hasnt trained in over a couple months but is still
 * paying, flag it but kind of ignore it?"
 *
 * Both halves of that matter. FLAG: they are real, they are counted, and they open
 * their names like any other count (SITE-RULES 11e). IGNORE: they are taken OUT of
 * the chase lists. Somebody eleven months gone is not this week's phone call, and
 * leaving them in "Going quiet" buries the members who drifted last fortnight —
 * the ones a call still reaches — under people it will not.
 *
 * Sixty days: "a couple of months", in his words, and comfortably past the longest
 * absence window the risk model will draw for even a fortnightly trainer (42).
 * Somebody this far out is not a rhythm that slipped, which is the thing the score
 * is about. It is a different question, so it gets a different list.
 *
 * WHY THIS DOES NOT LOOK AT WHAT THEY PAY, despite "still paying" being the phrase.
 * A floor seat is sent no price and no plan — the source redacts both — so a rule
 * reading priceCents would make the same member dormant for an Owner and not for a
 * Coach, and the two would see different counts of the same gym. `status === 'active'`
 * is the gym's own record that somebody is on the books, it means the same thing to
 * every seat, and it is the honest version of the question.
 */
export const DORMANT_DAYS = 60;
export function isDormant(m, today = todayISO()) {
    if (m.suppression)
        return false; // away on purpose, and already held
    if (m.status !== 'active')
        return false; // suspended or gone is a different fact
    const joined = daysSince(m.joinedAt, today);
    if (joined != null && joined <= ONBOARDING_DAYS)
        return false; // still arriving
    const away = daysSince(m.lastVisit, today);
    /* NO VISIT ON RECORD IS NOT EVIDENCE OF ABSENCE. It is just as likely to be a gym
       whose attendance never imported, and both real tenants are exactly that: an
       archived snapshot with gaps. This branch returned TRUE in the first version and
       CI caught it within the hour — every member of the API's fixture has a join date
       and no visit rows, so the whole roster went dormant and the Going quiet segment
       emptied.
   
       Which is the failure worth being careful about, because dormancy REMOVES people
       from every list the floor works from. Scoring somebody Red on missing data is
       recoverable — they are in front of a human, who can see the record is empty.
       Hiding them on missing data is not: nobody is looking at a list they are not on.
       So dormancy needs a date we actually hold, and "we have never seen them" is left
       where it is. */
    if (away == null)
        return false;
    return away >= DORMANT_DAYS;
}
export const SEGMENTS = [
    {
        id: 's1', name: 'Going quiet', sendable: true,
        desc: 'Members drifting from their own rhythm. A call from their anchor, this week.',
        rule: { type: 'tier', tier: 'Red' },
    },
    {
        id: 's2', name: 'Watch', sendable: true,
        desc: 'Members whose rhythm has started to slip. A warm word from their anchor, with no ask in it.',
        rule: { type: 'tier', tier: 'Amber' },
    },
    {
        id: 's3', name: 'New members (first 90 days)', sendable: true,
        desc: 'Members still building a rhythm. On the 90-day track, met on evidence, never scored.',
        rule: { type: 'onboarding' },
    },
    {
        id: 's4', name: 'Champions', sendable: true,
        desc: 'Members in a steady rhythm and known by two or more people here. The ones to ask for a referral or a story.',
        rule: { type: 'tier', tier: 'Green', minAnchors: 2 },
    },
    {
        id: 's5', name: 'Known to nobody', sendable: true,
        desc: 'Members with no anchor on record. An introduction in the room, not a message.',
        rule: { type: 'no_anchor' },
    },
    {
        id: 's6', name: 'Win-back', sendable: true,
        desc: 'Former members. A personal note from whoever knew them best.',
        rule: { type: 'status', value: ['cancelled'] },
    },
    {
        id: 's8', name: 'Dormant, still on the books', sendable: false,
        desc: 'Active memberships with no visit for two months or more. Counted and named '
            + 'here, and deliberately kept out of the lists the floor works from.',
        rule: { type: 'dormant' },
    },
    {
        id: 's7', name: 'Care Track and Hold', sendable: false,
        desc: 'Away, not gone. This list is for review, so nobody is forgotten. It is never an audience.',
        rule: { type: 'suppressed' },
    },
];
/**
 * Who is in a segment right now. Nothing is stored: a segment is a question asked of
 * the roster, and the answer moves when the roster does.
 *
 * Risk-ranked segments come back the way the consoles rank, WORST FIRST, because the
 * score counts down from 100.
 */
export function segmentMembers(all, rule, o = {}) {
    const today = o.today ?? todayISO();
    const score = (m) => (m.risk ?? riskModel(m, { today, ...(o.model ? { model: o.model } : {}) })).score;
    const tierOf = (m) => (m.risk ?? riskModel(m, { today, ...(o.model ? { model: o.model } : {}) })).tier;
    const byRisk = (list) => list.slice().sort((a, b) => score(a) - score(b) || a.name.localeCompare(b.name));
    const onboarding = (m) => {
        const d = daysSince(m.joinedAt, today);
        return d != null && d <= ONBOARDING_DAYS;
    };
    /* Split FIRST, before any rule can pick them up. */
    if (rule.type === 'suppressed')
        return all.filter((m) => m.suppression);
    const eligible = all.filter((m) => !m.suppression && m.status !== 'suspended');
    const active = eligible.filter((m) => m.status === 'active');
    const allScorable = active.filter((m) => !onboarding(m));
    /* DORMANT COMES OUT BEFORE ANY LIST THE FLOOR WORKS FROM. They are flagged, in
       their own segment, and they are not this week's calls — see the note above
       isDormant. Taking them out here rather than in each rule means a rule added
       later cannot forget to. */
    const dormant = allScorable.filter((m) => isDormant(m, today));
    const dormantIds = new Set(dormant.map((m) => m.id));
    const scorable = allScorable.filter((m) => !dormantIds.has(m.id));
    switch (rule.type) {
        case 'tier':
            return byRisk(scorable.filter((m) => tierOf(m) === rule.tier && m.anchors.length >= (rule.minAnchors ?? 0)));
        case 'onboarding':
            return active.filter(onboarding)
                .sort((a, b) => (daysSince(a.joinedAt, today) ?? 0) - (daysSince(b.joinedAt, today) ?? 0));
        case 'no_anchor':
            return byRisk(scorable.filter((m) => !m.anchors.length));
        case 'status':
            return eligible.filter((m) => rule.value.includes(m.status))
                .sort((a, b) => String(b.cancelledAt ?? '').localeCompare(String(a.cancelledAt ?? '')));
        /* Longest away first, and NOT by score. Every other list here is ranked by how
           much somebody needs a person this week; this one is a register, so it is
           ordered by the only fact it is about. */
        case 'dormant':
            return dormant.slice().sort((a, b) => String(a.lastVisit ?? '').localeCompare(String(b.lastVisit ?? '')));
    }
}
/* ── WHETHER A SCORE MAY EXIST AT ALL ──────────────────────────────────────
   These two are one rule seen from two sides, and they are deliberately in one
   place. `notScoredBecause` is what a SCREEN prints; `isScored` is what a SOURCE
   asks before it stores a number.

   THE DIVERGENCE THIS CLOSES (PRD 4). PRD 4 says a score is "never computed for a
   suppressed member or one inside their first 90 days". For suppression that was
   literally true - the stored risk was null. For the first 90 days it was not: the
   score was computed, stored, and then hidden by every screen that remembered to
   call `notScoredBecause`. The member record did not remember, and printed 63 ·
   Amber and an Intervention Ladder step for somebody the product says has no
   number. The same held for a frozen member.

   A NUMBER THAT EXISTS AND IS HIDDEN IS A NUMBER WAITING FOR ONE SCREEN TO FORGET.
   So the rule moved to where the number is written rather than where it is read.
   Closed 18 September 2026. */
/** Why a member holds no score, in the words the screen should print. Null when they
 *  are scored like anybody else. */
export function notScoredBecause(m, today = todayISO()) {
    if (m.suppression)
        return m.suppression.kind === 'hold' ? 'On hold' : 'Care Track';
    if (m.status === 'cancelled')
        return 'Former member';
    if (m.status === 'suspended')
        return 'Frozen';
    const d = daysSince(m.joinedAt, today);
    if (d != null && d <= ONBOARDING_DAYS)
        return 'First 90 days';
    return null;
}
/**
 * Whether the product may hold a score for this person at all. The one place a
 * source asks, so the seed, a rescore after a write and the API cannot drift.
 *
 * Cancellation initiated is here and not in `notScoredBecause` on purpose: that
 * function answers "what does the screen print instead of a number", and a member
 * who has asked to cancel gets something quite different in that space - what they
 * asked, in the words it was heard in. They still hold no score.
 */
export function isScored(m, today = todayISO()) {
    return notScoredBecause(m, today) == null && !cancellationOpen(m);
}
